/**
 * Approvals hub — single entry point for everything that needs an approver's
 * eyes. Top-level tabs:
 *   - ใบลา / OT          (all approver roles: SUPERVISOR + HR + OWNER)
 *   - ลาฉุกเฉิน          (HR/OWNER)
 *   - คำขอแก้ข้อมูล      (HR/OWNER)
 *   - ร้องเรียน / Tickets (HR/OWNER)
 *
 * Each tab renders a content-only component lifted from the former
 * /hr/emergency-leave, /hr/pending, /hr/tickets pages. URL hash
 * (#leave-ot|emergency|changes|tickets) preserves deep links.
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { HubHeader, PillTabs, roleLabelTh } from '../components/layout/HubHeader';
import { usePendingApprovals, useActOnApproval, useMedicalCertificateCheck, useMenuBadgeCounts, useUpcomingApproved, useRequestCancelApproved, useDecideShiftChange, useDecideSwap, usePendingAttendanceCorrections, useDecideAttendanceCorrection, useDecideShiftTime, useApprovedShiftTimes, useCancelShiftTime, type ShiftTimeRow } from '../hooks';
import { resolveFileUrl } from '../lib/api-client';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/toast';
import { ComplianceAlertList, type ComplianceItem } from '../components/ui/ComplianceAlert';
import { EmptyState } from '../components/ui/EmptyState';
import { useAuthStore } from '../lib/store';
import { HREmergencyLeaveContent } from './hr/EmergencyLeave';
import { HRPendingContent } from './hr/Pending';
import { HRTicketsContent } from './hr/Tickets';
import { Check, X, MessageCircle, ChevronDown, ChevronUp, AlertTriangle, Paperclip, CheckCircle2, ArrowRight, XCircle } from 'lucide-react';

type TopTab = 'leave-ot' | 'upcoming' | 'emergency' | 'changes' | 'tickets';
const VALID_TOP_TABS: TopTab[] = ['leave-ot', 'upcoming', 'emergency', 'changes', 'tickets'];

interface PendingLeave {
  leave_id: string;
  emp_code: string;
  emp_name?: string;
  date: string;
  leave_type: string;
  reason: string | null;
  current_level: number;
  is_backdated: boolean;
  is_emergency?: boolean;
  period?: 'full' | 'morning' | 'afternoon' | 'custom';
  start_time?: string | null;
  end_time?: string | null;
  evidence_url?: string | null;
  submitted_at?: string;
}

interface PendingOT {
  ot_id: string;
  emp_code: string;
  emp_name?: string;
  date: string;
  ot_type: string;
  start_time: string;
  end_time: string;
  reason: string;
  current_level: number;
  submitted_at?: string;
}

interface PendingShiftChange {
  id: string;
  emp_code: string;
  emp_name?: string;
  original_date: string;
  target_date: string;
  reason: string | null;
  is_urgent?: boolean;
}
// fb#171: สลับวันหยุดกับเพื่อน — คู่สลับยอมแล้ว รอหัวหน้า/HR อนุมัติ
interface PendingShiftSwap {
  id: string;
  date: string;
  requester_emp_code: string;
  requester_name?: string;
  requester_shift_name: string | null;
  target_emp_code: string;
  target_name?: string;
  target_shift_name: string | null;
  reason: string | null;
}
interface Pending {
  leaves: PendingLeave[];
  ots: PendingOT[];
  shift_changes: PendingShiftChange[];
  shift_swaps?: PendingShiftSwap[];
  shift_times?: ShiftTimeRow[]; // fb#609
}

function fmtChangeDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00+07:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', timeZone: 'Asia/Bangkok' }).format(d);
}

const LEAVE_TYPE_LABEL: Record<string, string> = {
  sick: 'ลาป่วย',
  personal: 'ลากิจ',
  vacation: 'ลาพักร้อน',
  unpaid: 'ลาไม่รับเงิน',
  maternity: 'ลาคลอด',
};

const PERIOD_LABEL: Record<string, string> = {
  full: 'เต็มวัน',
  morning: 'ครึ่งวันเช้า',
  afternoon: 'ครึ่งวันบ่าย',
  custom: 'ระบุเวลาเอง',
};

/**
 * ข้อควรตรวจก่อนอนุมัติใบลา (Non-HR UX §4) — ช่วยผู้อนุมัติที่ไม่ใช่ HR ไม่ให้
 * อนุมัติผ่านสิ่งที่ควรขอหลักฐาน/ตรวจก่อน. เบาๆ inline — ไม่บล็อกปุ่มอนุมัติ
 * (อนุมัติลาเป็น action ที่ทำบ่อย ต้องคงคลิกเดียว — frictionless).
 */
function leaveAlerts(l: PendingLeave): ComplianceItem[] {
  const items: ComplianceItem[] = [];
  if (l.leave_type === 'sick' && !l.evidence_url) {
    items.push({
      level: 'warn',
      title: 'ลาป่วยไม่มีใบรับรองแพทย์แนบ',
      detail: 'ลาป่วยตั้งแต่ 3 วันทำงานขึ้นไป นายจ้างขอใบรับรองแพทย์ได้ — ถ้าเข้าเงื่อนไขควรขอหลักฐานก่อนอนุมัติ',
      cite: 'พ.ร.บ.คุ้มครองแรงงาน ม.32',
    });
  }
  if (l.is_backdated) {
    items.push({
      level: 'warn',
      title: 'ลาย้อนหลัง',
      detail: 'คำขอนี้ลงวันที่ย้อนหลัง — ตรวจว่ามีเหตุผลรองรับก่อนอนุมัติ',
    });
  }
  // fb#484: ป้าย "ลาฉุกเฉิน" ไม่เคยบอกว่าแปลว่าอะไร HR เลยไปหาในแท็บลาฉุกเฉินแล้วเจอว่าง
  if (l.is_emergency) {
    items.push({
      level: 'warn',
      title: 'ลาฉุกเฉิน (ยื่นกระชั้นชิด)',
      detail: l.leave_type === 'sick'
        ? 'ยื่นลาป่วยหลังเริ่มวันลาไปแล้ว หรือล่วงหน้าไม่ถึง 1 ชั่วโมง — อนุมัติหรือไม่อนุมัติได้ที่ปุ่มด้านล่างตามปกติ'
        : 'ยื่นลากิจล่วงหน้าไม่ถึง 3 วัน — ตรวจเหตุผลแล้วอนุมัติหรือไม่อนุมัติได้ที่ปุ่มด้านล่างตามปกติ',
    });
  }
  return items;
}

function MedicalCertificateFlag({ leaveId, enabled }: { leaveId: string; enabled: boolean }) {
  const { data } = useMedicalCertificateCheck(leaveId, enabled);
  const decision = data?.decision;
  if (!decision) return null;
  const presentation = decision.flag === 'green'
    ? { label: 'ผ่าน', className: 'border-green-200 bg-green-50 text-green-800', Icon: CheckCircle2 }
    : decision.flag === 'yellow'
      ? { label: 'ตรวจสอบ', className: 'border-amber-200 bg-amber-50 text-amber-800', Icon: AlertTriangle }
      : { label: 'ไม่ตรง', className: 'border-red-200 bg-red-50 text-red-800', Icon: XCircle };
  const Icon = presentation.Icon;
  return (
    <div className={`mb-3 flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${presentation.className}`} role="status">
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="font-medium shrink-0">{presentation.label}</span>
      <span>{decision.message}</span>
    </div>
  );
}

/**
 * fb#158 — แยกคำขอตามเดือนของวันลา/วัน OT (TN: พนักงานยื่นลาล่วงหน้าหลายเดือนปนกันยาว
 * HR กดอนุมัติเมื่อถึงวันลาจริง หาใบของเดือนนี้ยาก) · ค่าเริ่มต้น = เดือนเก่าสุดที่ค้าง
 * (ไม่ซ่อนใบค้างเก่า) · "ทั้งหมด" ยังกดดูรวมได้
 */
const TH_MONTH_SHORT = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return `${TH_MONTH_SHORT[m ?? 0] ?? ym} ${String(((y ?? 0) + 543) % 100).padStart(2, '0')}`;
}
function MonthChips({ dates, value, onChange }: { dates: string[]; value: string; onChange: (v: string) => void }) {
  const counts = new Map<string, number>();
  for (const d of dates) { const k = d.slice(0, 7); counts.set(k, (counts.get(k) ?? 0) + 1); }
  const months = [...counts.keys()].sort();
  if (months.length < 2) return null;
  const chip = (k: string, label: string, n: number) => (
    <button key={k} type="button" onClick={() => onChange(k)}
      className={`shrink-0 rounded-full px-3 min-h-[36px] text-sm border ${value === k ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>
      {label} ({n})
    </button>
  );
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 mb-2">
      {months.map((k) => chip(k, monthLabel(k), counts.get(k)!))}
      {chip('all', 'ทั้งหมด', dates.length)}
    </div>
  );
}
function pickMonth<T>(rows: T[], date: (r: T) => string, sel: string): T[] {
  const months = [...new Set(rows.map((r) => date(r).slice(0, 7)))].sort();
  const eff = sel === 'all' || months.includes(sel) ? sel : (months[0] ?? 'all');
  return rows.filter((r) => eff === 'all' || date(r).startsWith(eff)).sort((a, b) => date(a).localeCompare(date(b)));
}
function effMonth(dates: string[], sel: string): string {
  const months = [...new Set(dates.map((d) => d.slice(0, 7)))].sort();
  return sel === 'all' || months.includes(sel) ? sel : (months[0] ?? 'all');
}

/** Inner content for the "ใบลา / OT" tab (was the standalone Approvals page). */
function LeaveOtApprovalsContent() {
  const { data, isLoading, isError, refetch } = usePendingApprovals() as { data: Pending | undefined; isLoading: boolean; isError: boolean; refetch: () => void };
  const act = useActOnApproval();
  const decideChange = useDecideShiftChange();
  const decideSwap = useDecideSwap();
  const dayoffCount = (data?.shift_changes?.length ?? 0) + (data?.shift_swaps?.length ?? 0);
  const shiftTimeCount = data?.shift_times?.length ?? 0;
  const decideTime = useDecideShiftTime();
  const cancelTime = useCancelShiftTime();
  const myRole = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const canReviewMedicalCertificate = myRole === 'HR' || myRole === 'OWNER';
  // fb#429: รับรองเวลา 2 ขั้นเหมือนใบลา — backend คืน OWNER/HR = ทุกใบ · หัวหน้า = ขั้น 1 ของลูกทีม · คนอื่น = []
  // (หัวหน้าส่วนใหญ่เป็นพนักงานทั่วไป เข้าหน้านี้จากลิงก์ใน LINE)
  const canCertify = myRole === 'HR' || myRole === 'OWNER';
  const [tab, setTab] = useState<'leave' | 'ot' | 'shift_change' | 'shift_time' | 'attendance_correction'>('leave');
  // fb#609: HR/เจ้าของ ยกเลิกใบเปลี่ยนเวลาที่อนุมัติแล้วได้ (งวดยังไม่ล็อก)
  const approvedTimes = useApprovedShiftTimes(canCertify && tab === 'shift_time');
  const { data: corrections, isError: correctionsError, refetch: refetchCorrections } = usePendingAttendanceCorrections();
  const decideCorrection = useDecideAttendanceCorrection();
  // fb#79: ไม่มีใบลา/OT/ย้ายวันหยุดค้าง แต่มีคำขอรับรองเวลา → เปิดแท็บรับรองเวลาให้เลย (เดิม HR หาไม่เจอ)
  const [autoTabbed, setAutoTabbed] = useState(false);
  useEffect(() => {
    if (autoTabbed || !data || !corrections) return;
    setAutoTabbed(true);
    const others = data.leaves.length + data.ots.length + (data.shift_changes?.length ?? 0) + (data.shift_swaps?.length ?? 0) + (data.shift_times?.length ?? 0);
    if (others === 0 && corrections.length > 0) setTab('attendance_correction');
  }, [autoTabbed, data, corrections]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [leaveMonth, setLeaveMonth] = useState('');
  const [otMonth, setOtMonth] = useState('');
  const [changeRejectId, setChangeRejectId] = useState<string | null>(null);
  const [changeRejectReason, setChangeRejectReason] = useState('');
  const [swapRejectId, setSwapRejectId] = useState<string | null>(null);
  const [swapRejectReason, setSwapRejectReason] = useState('');
  const [corrRejectId, setCorrRejectId] = useState<string | null>(null);
  const [timeReject, setTimeReject] = useState<{ id: string; mode: 'reject' | 'cancel' } | null>(null);
  const [timeRejectReason, setTimeRejectReason] = useState('');
  const [corrRejectReason, setCorrRejectReason] = useState('');
  const KIND_LABEL_C: Record<string, string> = { morning_in: 'เข้างาน (เช้า)', evening_out: 'เลิกงาน (เย็น)' };
  const toast = useToast();

  async function approveCorrection(id: string, expected_status: 'pending_supervisor' | 'pending') {
    try {
      const res = await decideCorrection.mutateAsync({ id, decision: 'approved', expected_status }) as { status?: string } | undefined;
      toast.success(res?.status === 'pending' ? 'อนุมัติแล้ว — ส่งต่อให้ HR อนุมัติขั้นสุดท้าย' : 'รับรองเวลาแล้ว — บันทึกลงเวลาเข้างานเรียบร้อย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  async function submitCorrReject() {
    if (!corrRejectId || !corrRejectReason.trim()) { toast.error('ระบุเหตุผลที่ไม่อนุมัติ'); return; }
    try {
      const st = corrections?.find((c) => c.id === corrRejectId)?.status === 'pending_supervisor' ? 'pending_supervisor' : 'pending';
      await decideCorrection.mutateAsync({
        id: corrRejectId, decision: 'rejected', rejection_reason: corrRejectReason.trim(), expected_status: st,
      });
      setCorrRejectId(null); setCorrRejectReason('');
      toast.success('ไม่อนุมัติคำขอแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function approveChange(id: string) {
    try {
      await decideChange.mutateAsync({ id, decision: 'approved' });
      toast.success('อนุมัติแล้ว — ย้ายวันหยุดเรียบร้อย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  async function submitChangeReject() {
    if (!changeRejectId || !changeRejectReason.trim()) { toast.error('ระบุเหตุผลที่ไม่อนุมัติ'); return; }
    try {
      await decideChange.mutateAsync({ id: changeRejectId, decision: 'rejected', rejection_reason: changeRejectReason.trim() });
      setChangeRejectId(null); setChangeRejectReason('');
      toast.success('ไม่อนุมัติคำขอแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  async function approveTime(id: string) {
    try {
      await decideTime.mutateAsync({ id, decision: 'approved' });
      toast.success('อนุมัติแล้ว — วันนั้นใช้เวลาเข้า-ออกงานใหม่ทันที');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  async function submitTimeReject() {
    if (!timeReject || !timeRejectReason.trim()) { toast.error(timeReject?.mode === 'cancel' ? 'ระบุเหตุผลที่ยกเลิก' : 'ระบุเหตุผลที่ไม่อนุมัติ'); return; }
    try {
      if (timeReject.mode === 'cancel') {
        await cancelTime.mutateAsync({ id: timeReject.id, reason: timeRejectReason.trim() });
        toast.success('ยกเลิกแล้ว — วันนั้นกลับไปใช้เวลากะเดิม');
      } else {
        await decideTime.mutateAsync({ id: timeReject.id, decision: 'rejected', rejection_reason: timeRejectReason.trim() });
        toast.success('ไม่อนุมัติคำขอแล้ว');
      }
      setTimeReject(null); setTimeRejectReason('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  async function approveSwap(id: string) {
    try {
      await decideSwap.mutateAsync({ id, decision: 'approved' });
      toast.success('อนุมัติแล้ว — สลับวันหยุดลงตารางกะเรียบร้อย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  async function submitSwapReject() {
    if (!swapRejectId || !swapRejectReason.trim()) { toast.error('ระบุเหตุผลที่ไม่อนุมัติ'); return; }
    try {
      await decideSwap.mutateAsync({ id: swapRejectId, decision: 'rejected', decision_note: swapRejectReason.trim() });
      setSwapRejectId(null); setSwapRejectReason('');
      toast.success('ไม่อนุมัติคำขอแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }
  // Reason modal — used by both "reject" and "request more info".
  const [reasonModal, setReasonModal] = useState<{ kind: 'leave' | 'ot'; id: string; action: 'reject' | 'request_info' } | null>(null);
  const [reasonText, setReasonText] = useState('');

  // fb#606: ใบลาตกงวดที่ปิดจ่ายแล้ว → ถามยืนยัน "บันทึกสถิติการลาอย่างเดียว" แทนบังคับให้ปฏิเสธ
  const [lockedLeave, setLockedLeave] = useState<{ id: string; period: string } | null>(null);
  async function approve(kind: 'leave' | 'ot', id: string, lockedPeriodRecordOnly = false) {
    try {
      await act.mutateAsync({ kind, id, action: 'approve', ...(lockedPeriodRecordOnly ? { locked_period_record_only: true } : {}) });
      setLockedLeave(null);
      toast.success(lockedPeriodRecordOnly ? 'อนุมัติแล้ว — บันทึกเป็นสถิติการลา ไม่ย้อนไปคิดเงินงวดที่ปิดแล้ว' : 'อนุมัติแล้ว');
    } catch (e) {
      const err = e as { code?: string; details?: { period?: string; can_record_only?: boolean } };
      if (kind === 'leave' && !lockedPeriodRecordOnly && err.code === 'PERIOD_LOCKED' && err.details?.can_record_only) {
        setLockedLeave({ id, period: err.details.period ?? '' });
        return;
      }
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  function openReason(kind: 'leave' | 'ot', id: string, action: 'reject' | 'request_info') {
    setReasonModal({ kind, id, action });
    setReasonText('');
  }

  async function submitReason() {
    if (!reasonModal || !reasonText.trim()) {
      toast.error(reasonModal?.action === 'reject' ? 'ระบุเหตุผลที่ปฏิเสธ' : 'ระบุข้อความที่ต้องการสอบถาม');
      return;
    }
    const { kind, id, action } = reasonModal;
    try {
      await act.mutateAsync(
        action === 'reject'
          ? { kind, id, action: 'reject', reason: reasonText.trim() }
          : { kind, id, action: 'request_info', info_message: reasonText.trim() },
      );
      setReasonModal(null);
      toast.success(action === 'reject' ? 'ปฏิเสธคำขอแล้ว' : 'ส่งคำขอข้อมูลเพิ่มเติมแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  if (isLoading) return <p>กำลังโหลด…</p>;

  return (
    <>
      {/* fb#431: โหลดไม่ผ่าน (เช่นไม่มีสิทธิ์) ต้องบอก — เดิมเงียบแล้วโชว์ ใบลา (0) ทั้งที่มีใบค้าง */}
      {isError && (
        <div className="card p-4 mb-3 text-sm text-red-700 flex items-center justify-between gap-2">
          <span>โหลดรายการรออนุมัติไม่สำเร็จ</span>
          <button className="btn-secondary" onClick={() => refetch()}>ลองใหม่</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-3">
        <button onClick={() => setTab('leave')} className={tab === 'leave' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
          ใบลา ({data?.leaves.length ?? 0})
        </button>
        <button onClick={() => setTab('ot')} className={tab === 'ot' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
          OT ({data?.ots.length ?? 0})
        </button>
        <button onClick={() => setTab('shift_change')} className={tab === 'shift_change' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
          ย้ายวันหยุด ({dayoffCount})
        </button>
        <button onClick={() => setTab('shift_time')} className={tab === 'shift_time' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
          เปลี่ยนเวลาเข้างาน ({shiftTimeCount})
        </button>
        {(canCertify || correctionsError || (corrections?.length ?? 0) > 0) && (
          <button onClick={() => setTab('attendance_correction')} className={tab === 'attendance_correction' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>
            รับรองเวลา ({corrections?.length ?? 0})
          </button>
        )}
      </div>

      {tab === 'leave' && (
        <MonthChips dates={(data?.leaves ?? []).map((l) => l.date)} value={effMonth((data?.leaves ?? []).map((l) => l.date), leaveMonth)} onChange={setLeaveMonth} />
      )}
      {tab === 'ot' && (
        <MonthChips dates={(data?.ots ?? []).map((o) => o.date)} value={effMonth((data?.ots ?? []).map((o) => o.date), otMonth)} onChange={setOtMonth} />
      )}
      <div className="space-y-2">
        {tab === 'leave' && pickMonth(data?.leaves ?? [], (l) => l.date, leaveMonth).map((l) => {
          const isOpen = expanded === `l:${l.leave_id}`;
          return (
            <div key={l.leave_id} className="card p-4">
              <div className="flex justify-between mb-2 gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{l.emp_name ?? l.emp_code}</p>
                  <p className="text-xs text-gray-500">{l.emp_code} · {l.leave_id}</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {LEAVE_TYPE_LABEL[l.leave_type] ?? l.leave_type}
                    {l.period && l.period !== 'full' && ` · ${PERIOD_LABEL[l.period]}`}
                    {' · '}
                    {l.date}
                  </p>
                </div>
                <span className="badge-info shrink-0 self-start">L{l.current_level}</span>
              </div>

              <div className="flex gap-1 flex-wrap mb-2">
                {l.is_emergency && (
                  <span className="badge bg-red-100 text-red-700 flex items-center gap-0.5">
                    <AlertTriangle className="w-3 h-3" /> ลาฉุกเฉิน
                  </span>
                )}
                {l.is_backdated && <span className="badge-warn">ลาย้อนหลัง</span>}
              </div>

              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : `l:${l.leave_id}`)}
                className="text-sm text-brand-700 mb-3 flex items-center gap-1 hover:underline"
              >
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {isOpen ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
              </button>

              {isOpen && (
                <div className="rounded-lg bg-gray-50 p-3 mb-3 space-y-2 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">เหตุผล</div>
                    <div className="text-gray-900 whitespace-pre-line">{l.reason || '— ไม่ระบุ —'}</div>
                  </div>
                  {l.period === 'custom' && (l.start_time || l.end_time) && (
                    <div>
                      <div className="text-xs text-gray-500">เวลาที่ลา</div>
                      <div>{l.start_time ?? '?'} – {l.end_time ?? '?'}</div>
                    </div>
                  )}
                  {l.evidence_url && (
                    <div>
                      <a
                        href={resolveFileUrl(l.evidence_url) ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-700 flex items-center gap-1 hover:underline"
                      >
                        <Paperclip className="w-4 h-4" /> ดูหลักฐานแนบ
                      </a>
                    </div>
                  )}
                  {l.submitted_at && (
                    <div className="text-xs text-gray-500">
                      ส่งเมื่อ {new Date(l.submitted_at).toLocaleString('th-TH')}
                    </div>
                  )}
                </div>
              )}

              {leaveAlerts(l).length > 0 && (
                <div className="mb-3">
                  <ComplianceAlertList items={leaveAlerts(l)} />
                </div>
              )}

              <MedicalCertificateFlag leaveId={l.leave_id} enabled={isOpen && !!l.evidence_url && canReviewMedicalCertificate} />
              <div className="flex gap-2">
                <button onClick={() => approve('leave', l.leave_id)} className="btn-cta flex-1">
                  <Check className="w-4 h-4" /> อนุมัติ
                </button>
                <button onClick={() => openReason('leave', l.leave_id, 'request_info')} className="btn-secondary" title="ขอข้อมูลเพิ่ม">
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button onClick={() => openReason('leave', l.leave_id, 'reject')} className="btn-danger" title="ปฏิเสธ">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {tab === 'ot' && pickMonth(data?.ots ?? [], (o) => o.date, otMonth).map((o) => {
          const isOpen = expanded === `o:${o.ot_id}`;
          return (
            <div key={o.ot_id} className="card p-4">
              <div className="flex justify-between mb-2 gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{o.emp_name ?? o.emp_code}</p>
                  <p className="text-xs text-gray-500">{o.emp_code} · {o.ot_id}</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {o.ot_type} · {o.date} · {o.start_time}–{o.end_time}
                  </p>
                </div>
                <span className="badge-info shrink-0 self-start">L{o.current_level}</span>
              </div>

              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : `o:${o.ot_id}`)}
                className="text-sm text-brand-700 mb-3 flex items-center gap-1 hover:underline"
              >
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {isOpen ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
              </button>

              {isOpen && (
                <div className="rounded-lg bg-gray-50 p-3 mb-3 space-y-2 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">เหตุผล</div>
                    <div className="text-gray-900 whitespace-pre-line">{o.reason || '—'}</div>
                  </div>
                  {o.submitted_at && (
                    <div className="text-xs text-gray-500">
                      ส่งเมื่อ {new Date(o.submitted_at).toLocaleString('th-TH')}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => approve('ot', o.ot_id)} className="btn-cta flex-1">
                  <Check className="w-4 h-4" /> อนุมัติ
                </button>
                <button onClick={() => openReason('ot', o.ot_id, 'request_info')} className="btn-secondary" title="ขอข้อมูลเพิ่ม">
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button onClick={() => openReason('ot', o.ot_id, 'reject')} className="btn-danger" title="ปฏิเสธ">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {tab === 'shift_change' && (data?.shift_changes ?? []).map((c) => (
          <div key={c.id} className="card p-4">
            <div className="flex justify-between mb-2 gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate flex items-center gap-1.5">
                  {c.emp_name ?? c.emp_code}
                  {c.is_urgent && <span className="badge-warn shrink-0">กระทันหัน</span>}
                </p>
                <p className="text-xs text-gray-500">{c.emp_code}</p>
                <p className="text-sm text-gray-700 mt-1 flex items-center gap-1.5 flex-wrap">
                  <span>หยุด {fmtChangeDate(c.original_date)}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                  <span>หยุด {fmtChangeDate(c.target_date)}</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">วันหยุดเดิมกลับมาทำงาน · วันใหม่หยุดแทน</p>
              </div>
            </div>
            {c.reason && <p className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded">เหตุผล: {c.reason}</p>}
            <div className="flex gap-2">
              <button onClick={() => approveChange(c.id)} disabled={decideChange.isPending} className="btn-cta flex-1 justify-center flex items-center gap-1">
                <Check className="w-4 h-4" /> อนุมัติ
              </button>
              <button onClick={() => { setChangeRejectId(c.id); setChangeRejectReason(''); }} className="btn-danger" title="ไม่อนุมัติ">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {tab === 'shift_change' && (data?.shift_swaps ?? []).map((w) => (
          <div key={w.id} className="card p-4">
            <div className="mb-2 min-w-0">
              <p className="font-semibold flex items-center gap-1.5 flex-wrap">
                <span className="badge-info shrink-0">สลับกับเพื่อน</span>
                <span>{fmtChangeDate(w.date)}</span>
              </p>
              <p className="text-sm text-gray-700 mt-1">
                {w.requester_name ?? w.requester_emp_code} <span className="text-xs text-gray-500">({w.requester_emp_code})</span>
                {' '}: {w.requester_shift_name ?? 'หยุด'} <ArrowRight className="inline w-3.5 h-3.5 text-gray-400" /> {w.target_shift_name ?? 'หยุด'}
              </p>
              <p className="text-sm text-gray-700">
                {w.target_name ?? w.target_emp_code} <span className="text-xs text-gray-500">({w.target_emp_code})</span>
                {' '}: {w.target_shift_name ?? 'หยุด'} <ArrowRight className="inline w-3.5 h-3.5 text-gray-400" /> {w.requester_shift_name ?? 'หยุด'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">คู่สลับตอบตกลงแล้ว · อนุมัติแล้วระบบสลับกะในตารางให้ทั้งสองคน</p>
            </div>
            {w.reason && <p className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded">เหตุผล: {w.reason}</p>}
            <div className="flex gap-2">
              <button onClick={() => approveSwap(w.id)} disabled={decideSwap.isPending} className="btn-cta flex-1 justify-center flex items-center gap-1">
                <Check className="w-4 h-4" /> อนุมัติ
              </button>
              <button onClick={() => { setSwapRejectId(w.id); setSwapRejectReason(''); }} className="btn-danger" title="ไม่อนุมัติ">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {tab === 'shift_time' && (data?.shift_times ?? []).map((t) => (
          <div key={t.id} className="card p-4">
            <div className="mb-2 min-w-0">
              <p className="font-semibold truncate">{t.emp_name ?? t.emp_code}</p>
              <p className="text-xs text-gray-500">{t.emp_code}</p>
              <p className="text-sm text-gray-700 mt-1 flex items-center gap-1.5 flex-wrap">
                <span>{fmtChangeDate(t.work_date)}</span>
                <span className="text-gray-400 line-through">{t.orig_start_time}–{t.orig_end_time}</span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                <b>{t.new_start_time}–{t.new_end_time}</b>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">ชั่วโมงเท่ากะเดิม · ตารางกะไม่เปลี่ยน · อนุมัติแล้วสแกน/สาย/ออกก่อนคิดตามเวลาใหม่</p>
            </div>
            <p className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded">เหตุผล: {t.reason}</p>
            <div className="flex gap-2">
              <button onClick={() => approveTime(t.id)} disabled={decideTime.isPending} className="btn-cta flex-1 justify-center flex items-center gap-1">
                <Check className="w-4 h-4" /> อนุมัติ
              </button>
              <button onClick={() => { setTimeReject({ id: t.id, mode: 'reject' }); setTimeRejectReason(''); }} className="btn-danger" title="ไม่อนุมัติ">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {tab === 'shift_time' && shiftTimeCount === 0 && (
          <EmptyState icon={CheckCircle2} title="ไม่มีคำขอเปลี่ยนเวลาเข้างาน" detail="คำขอเปลี่ยนเวลาเข้า-ออกงานรายวันของพนักงานจะมาแสดงที่นี่" />
        )}

        {tab === 'shift_time' && canCertify && (approvedTimes.data?.length ?? 0) > 0 && (
          <div className="pt-2 space-y-2">
            <p className="text-sm font-medium text-gray-700">อนุมัติแล้ว (ยกเลิกได้ถ้างวดยังไม่ปิด)</p>
            {(approvedTimes.data ?? []).map((t) => (
              <div key={t.id} className="card p-3 flex items-center justify-between gap-2">
                <div className="min-w-0 text-sm">
                  <p className="font-medium truncate">{t.emp_name ?? t.emp_code}</p>
                  <p className="text-gray-600">{fmtChangeDate(t.work_date)} · <b>{t.new_start_time}–{t.new_end_time}</b> <span className="text-xs text-gray-400">(เดิม {t.orig_start_time}–{t.orig_end_time})</span></p>
                </div>
                <button onClick={() => { setTimeReject({ id: t.id, mode: 'cancel' }); setTimeRejectReason(''); }} className="btn-secondary shrink-0 min-h-[44px]">
                  ยกเลิก
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === 'attendance_correction' && (corrections ?? []).map((c) => (
          <div key={c.id} className="card p-4">
            <div className="flex justify-between mb-2 gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{c.emp_name ?? c.emp_code}</p>
                <p className="text-xs text-gray-500">{c.emp_code}</p>
                <p className="text-sm text-gray-700 mt-1">
                  {fmtChangeDate(c.date)} · {KIND_LABEL_C[c.kind] ?? c.kind} · ขอรับรองเวลา <b>{c.requested_time} น.</b>
                </p>
              </div>
              <span className="badge-warn shrink-0 whitespace-nowrap self-start">
                {c.status === 'pending_supervisor' ? 'ขั้น 1/2 รอหัวหน้า' : 'ขั้น 2/2 รอ HR'}
              </span>
            </div>
            <p className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded">เหตุผล: {c.reason}</p>
            {c.evidence_url && (
              <a
                href={resolveFileUrl(c.evidence_url) ?? '#'}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-brand-700 flex items-center gap-1 hover:underline mb-2"
              >
                <Paperclip className="w-4 h-4" /> ดูหลักฐานแนบ
              </a>
            )}
            <div className="flex gap-2">
              <button onClick={() => approveCorrection(c.id, c.status === 'pending_supervisor' ? 'pending_supervisor' : 'pending')} disabled={decideCorrection.isPending} className="btn-cta flex-1 justify-center flex items-center gap-1">
                <Check className="w-4 h-4" /> {c.status === 'pending_supervisor' ? 'อนุมัติ ส่งต่อ HR' : 'รับรองเวลา'}
              </button>
              <button onClick={() => { setCorrRejectId(c.id); setCorrRejectReason(''); }} className="btn-danger" title="ไม่อนุมัติ">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {tab === 'attendance_correction' && correctionsError && (
          <div className="card p-4 text-sm text-red-700 flex items-center justify-between gap-2">
            <span>โหลดคำขอรับรองเวลาไม่สำเร็จ</span>
            <button className="btn-secondary" onClick={() => refetchCorrections()}>ลองใหม่</button>
          </div>
        )}

        {tab === 'attendance_correction' && !correctionsError && (corrections?.length ?? 0) === 0 && (
          <EmptyState
            icon={CheckCircle2}
            title="ไม่มีคำขอรับรองเวลา"
            detail="คำขอรับรองเวลาเข้า-ออกงานของพนักงานจะมาแสดงที่นี่"
          />
        )}

        {tab !== 'attendance_correction' && tab !== 'shift_time' && (data?.leaves.length === 0 && data?.ots.length === 0 && dayoffCount === 0) && (
          <EmptyState
            icon={CheckCircle2}
            title="ไม่มีคำขอที่รออนุมัติ"
            detail="เคลียร์หมดแล้ว — คำขอลา/OT/ย้ายวันหยุดใหม่จะมาแสดงที่นี่"
          />
        )}
      </div>

      <Modal
        open={reasonModal !== null}
        onClose={() => setReasonModal(null)}
        title={reasonModal?.action === 'reject' ? 'ปฏิเสธคำขอ' : 'ขอข้อมูลเพิ่มเติม'}
      >
        <div className="space-y-3">
          <div>
            <label className="label">{reasonModal?.action === 'reject' ? 'เหตุผลที่ปฏิเสธ' : 'ข้อความถึงพนักงาน'}</label>
            <textarea className="input" rows={3} value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="พิมพ์ข้อความ…" />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setReasonModal(null)}>ยกเลิก</button>
            <button
              className={`flex-1 ${reasonModal?.action === 'reject' ? 'btn-danger' : 'btn-primary'}`}
              onClick={submitReason}
              disabled={act.isPending}
            >
              {reasonModal?.action === 'reject' ? 'ปฏิเสธ' : 'ส่งคำขอ'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={lockedLeave !== null}
        onClose={() => setLockedLeave(null)}
        title="ใบลานี้อยู่ในงวดที่ปิดจ่ายแล้ว"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            งวด {lockedLeave?.period} ปิดจ่ายเงินไปแล้ว อนุมัติตอนนี้ได้ โดยระบบบันทึกวันลาเข้าสถิติและสิทธิ์การลาเท่านั้น
            ไม่ย้อนไปแก้ยอดเงินของงวดที่ปิดแล้ว
          </p>
          <p className="text-sm text-gray-500">
            ถ้าวันนั้นเคยถูกหักเงินไปแล้วและต้องคืน ให้ฝ่ายบุคคลตั้งเป็นรายการตกเบิกในงวดถัดไป
          </p>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setLockedLeave(null)}>ยกเลิก</button>
            <button
              className="btn-primary flex-1"
              onClick={() => lockedLeave && approve('leave', lockedLeave.id, true)}
              disabled={act.isPending}
            >
              อนุมัติเป็นสถิติการลา
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={changeRejectId !== null}
        onClose={() => setChangeRejectId(null)}
        title="ไม่อนุมัติคำขอย้ายวันหยุด"
      >
        <div className="space-y-3">
          <div>
            <label className="label">เหตุผลที่ไม่อนุมัติ</label>
            <textarea className="input" rows={3} value={changeRejectReason} onChange={(e) => setChangeRejectReason(e.target.value)} placeholder="พิมพ์เหตุผล…" maxLength={300} />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setChangeRejectId(null)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitChangeReject} disabled={decideChange.isPending}>
              ไม่อนุมัติ
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={timeReject !== null}
        onClose={() => setTimeReject(null)}
        title={timeReject?.mode === 'cancel' ? 'ยกเลิกใบเปลี่ยนเวลาที่อนุมัติแล้ว' : 'ไม่อนุมัติคำขอเปลี่ยนเวลา'}
      >
        <div className="space-y-3">
          <div>
            <label className="label">{timeReject?.mode === 'cancel' ? 'เหตุผลที่ยกเลิก (แจ้งพนักงาน)' : 'เหตุผลที่ไม่อนุมัติ'}</label>
            <textarea className="input" rows={3} value={timeRejectReason} onChange={(e) => setTimeRejectReason(e.target.value)} placeholder="พิมพ์เหตุผล…" maxLength={300} />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setTimeReject(null)}>ปิด</button>
            <button className="btn-danger flex-1" onClick={submitTimeReject} disabled={decideTime.isPending || cancelTime.isPending}>
              {timeReject?.mode === 'cancel' ? 'ยกเลิกใบนี้' : 'ไม่อนุมัติ'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={swapRejectId !== null}
        onClose={() => setSwapRejectId(null)}
        title="ไม่อนุมัติคำขอสลับวันหยุด"
      >
        <div className="space-y-3">
          <div>
            <label className="label">เหตุผลที่ไม่อนุมัติ</label>
            <textarea className="input" rows={3} value={swapRejectReason} onChange={(e) => setSwapRejectReason(e.target.value)} placeholder="พิมพ์เหตุผล…" maxLength={300} />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setSwapRejectId(null)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitSwapReject} disabled={decideSwap.isPending}>
              ไม่อนุมัติ
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={corrRejectId !== null}
        onClose={() => setCorrRejectId(null)}
        title="ไม่อนุมัติคำขอรับรองเวลา"
      >
        <div className="space-y-3">
          <div>
            <label className="label">เหตุผลที่ไม่อนุมัติ (พนักงานเห็นข้อความนี้)</label>
            <textarea className="input" rows={3} value={corrRejectReason} onChange={(e) => setCorrRejectReason(e.target.value)} placeholder="พิมพ์เหตุผล…" maxLength={300} />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setCorrRejectId(null)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitCorrReject} disabled={decideCorrection.isPending}>
              ไม่อนุมัติ
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * แท็บ "ยกเลิกที่อนุมัติแล้ว" — owner/HR ดูใบลา/OT ที่อนุมัติแล้ว แต่ยังมาไม่ถึง
 * แล้วขอยกเลิก (ต้องระบุเหตุผล) → ระบบส่งให้พนักงานกดยินยอมก่อนถึงจะยกเลิกจริง.
 */
function fmtThaiDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00+07:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(d);
}

function UpcomingApprovedContent() {
  const { data, isLoading } = useUpcomingApproved();
  const requestCancel = useRequestCancelApproved();
  const toast = useToast();
  const [modal, setModal] = useState<{ kind: 'leave' | 'ot'; record_id: string; who: string; what: string } | null>(null);
  const [reason, setReason] = useState('');

  const submit = async () => {
    if (!modal) return;
    if (reason.trim().length === 0) { toast.error('กรุณาระบุเหตุผล'); return; }
    try {
      await requestCancel.mutateAsync({ kind: modal.kind, record_id: modal.record_id, reason: reason.trim() });
      toast.success('ส่งคำขอยกเลิกแล้ว — รอพนักงานยินยอม');
      setModal(null);
      setReason('');
    } catch (e: any) {
      toast.error(e?.message ?? 'ส่งคำขอไม่สำเร็จ');
    }
  };

  if (isLoading) return <div className="text-center text-gray-400 py-10 text-sm">กำลังโหลด…</div>;

  const leaves = data?.leaves ?? [];
  const ots = data?.ots ?? [];
  if (leaves.length === 0 && ots.length === 0) {
    return <EmptyState title="ไม่มีใบลา/OT ที่อนุมัติแล้วและยังมาไม่ถึง" detail="ยกเลิกได้เฉพาะรายการที่อนุมัติแล้ว แต่วันยังไม่มาถึงเท่านั้น" />;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">ยกเลิกได้เฉพาะรายการที่ <strong>ยังมาไม่ถึง</strong> และต้องให้พนักงาน <strong>กดยินยอม</strong> ก่อนจึงจะยกเลิกจริง</p>

      {leaves.map((l) => (
        <div key={l.record_id} className="card p-3">
          <div className="flex justify-between items-start">
            <div className="min-w-0">
              <p className="font-medium">{l.emp_name}</p>
              <p className="text-sm text-gray-700">{l.type_label}{l.days_count > 1 ? ` ${l.days_count} วัน` : ''}</p>
              <p className="text-sm text-gray-500">
                {l.days_count > 1 ? `${fmtThaiDate(l.date)} – ${fmtThaiDate(l.end_date)}` : fmtThaiDate(l.date)}
              </p>
              {l.reason && <p className="text-sm text-gray-600 mt-1 line-clamp-2">"{l.reason}"</p>}
            </div>
          </div>
          {l.cancel_pending ? (
            <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">⏳ รอพนักงานยินยอมการยกเลิก{l.cancel_reason ? ` · ${l.cancel_reason}` : ''}</p>
          ) : (
            <button onClick={() => setModal({ kind: 'leave', record_id: l.record_id, who: l.emp_name, what: `${l.type_label} ${fmtThaiDate(l.date)}` })}
              className="btn-secondary w-full mt-2 text-sm text-red-600 border-red-200 hover:bg-red-50">ขอยกเลิกการลานี้</button>
          )}
        </div>
      ))}

      {ots.map((o) => (
        <div key={o.record_id} className="card p-3">
          <div className="min-w-0">
            <p className="font-medium">{o.emp_name}</p>
            <p className="text-sm text-gray-700">{o.type_label}{o.start_time && o.end_time ? ` ${o.start_time}-${o.end_time}` : ''}</p>
            <p className="text-sm text-gray-500">{fmtThaiDate(o.date)}</p>
            {o.reason && <p className="text-sm text-gray-600 mt-1 line-clamp-2">"{o.reason}"</p>}
          </div>
          {o.cancel_pending ? (
            <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">⏳ รอพนักงานยินยอมการยกเลิก{o.cancel_reason ? ` · ${o.cancel_reason}` : ''}</p>
          ) : (
            <button onClick={() => setModal({ kind: 'ot', record_id: o.record_id, who: o.emp_name, what: `${o.type_label} ${fmtThaiDate(o.date)}` })}
              className="btn-secondary w-full mt-2 text-sm text-red-600 border-red-200 hover:bg-red-50">ขอยกเลิก OT นี้</button>
          )}
        </div>
      ))}

      <Modal open={!!modal} onClose={() => { setModal(null); setReason(''); }} title="ขอยกเลิกรายการที่อนุมัติแล้ว">
        {modal && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">{modal.who} · {modal.what}</p>
            <p className="text-xs text-gray-500">พนักงานจะได้รับแจ้งและต้องกดยินยอมก่อน รายการถึงจะถูกยกเลิก</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
              placeholder="เหตุผลในการขอยกเลิก (เช่น มีงานด่วน ต้องให้มาทำงาน)" className="input w-full" />
            <button onClick={submit} disabled={requestCancel.isPending} className="btn-primary w-full justify-center disabled:opacity-50">
              {requestCancel.isPending ? 'กำลังส่ง…' : 'ส่งคำขอยกเลิก'}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}

/**
 * Top-level Approvals hub. Renders tabs (filtered by role) and delegates
 * to the matching content component. Keeps URL hash in sync so deep links
 * like /approvals#emergency from briefing/close-readiness still work.
 */
export function Approvals() {
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const isAdmin = role === 'OWNER' || role === 'HR' || role === 'MANAGER';
  const badges = useMenuBadgeCounts(role);

  const [topTab, setTopTab] = useState<TopTab>(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '') as TopTab;
    return VALID_TOP_TABS.includes(h) ? h : 'leave-ot';
  });

  // Keep the URL hash aligned with the active tab (replaceState avoids
  // polluting browser history when switching tabs).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${topTab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [topTab]);

  const canCancelApproved = role === 'OWNER' || role === 'HR';
  const tabs: Array<{ key: TopTab; label: string; count: number; visible: boolean }> = [
    { key: 'leave-ot', label: 'ใบลา / OT', count: badges.approvals, visible: true },
    { key: 'upcoming', label: 'ยกเลิกที่อนุมัติแล้ว', count: 0, visible: canCancelApproved },
    { key: 'emergency', label: 'ลาฉุกเฉิน', count: badges.emergency + badges.urgentLeaves, visible: isAdmin },
    { key: 'changes', label: 'คำขอแก้ข้อมูล', count: badges.pendingChanges, visible: isAdmin },
    { key: 'tickets', label: 'ร้องเรียน', count: badges.tickets, visible: isAdmin },
  ];
  const visibleTabs = tabs.filter((t) => t.visible);
  const activeTab = visibleTabs.some((t) => t.key === topTab) ? topTab : 'leave-ot';

  return (
    <AdminLayout title="อนุมัติคำขอ">
      <HubHeader
        section="เวลาทำงาน · ลา · OT"
        title={visibleTabs.find((t) => t.key === activeTab)?.label ?? 'อนุมัติคำขอ'}
        roleLabel={roleLabelTh(role)}
      />
      <PillTabs
        tabs={visibleTabs}
        active={activeTab}
        onChange={setTopTab}
        badges={Object.fromEntries(visibleTabs.map((t) => [t.key, t.count])) as Partial<Record<TopTab, number>>}
      />

      {activeTab === 'leave-ot' && <LeaveOtApprovalsContent />}
      {activeTab === 'upcoming' && <UpcomingApprovedContent />}
      {activeTab === 'emergency' && <HREmergencyLeaveContent onGoToLeaves={() => setTopTab('leave-ot')} />}
      {activeTab === 'changes' && <HRPendingContent />}
      {activeTab === 'tickets' && <HRTicketsContent />}
    </AdminLayout>
  );
}
